import type { Renter } from '@/utils/types/renter.interface';
import { RentCoverageTypes, SecurityDepositTypes } from '@/constants/CoverageType';
import { ScreeningResult } from '../constants';
import { formatCalendarDate } from '@/shared/utils/calendar-date.util';

const SCREENING_STATUS_MAP: Record<string, string> = {
  [ScreeningResult.Approved]: 'approved',
  [ScreeningResult.ApprovedWithConditions]: 'conditional',
  [ScreeningResult.Denied]: 'denied',
};

type CoverageDetail = {
  coverage_unit: string;
  coverage_amount: number;
};

type Coverage = {
  lease_guarantee?: CoverageDetail;
  security_deposit_replacement?: CoverageDetail;
};

const buildCoverage = (values: Renter): Coverage | undefined => {
  const isRentCustomized = values.rentCoverage === RentCoverageTypes.Customize;
  const isDepositCustomized =
    values.securityDepositCoverage === SecurityDepositTypes.CustomizeOrRemove;

  if (!isRentCustomized && !isDepositCustomized) return undefined;

  const coverage: Coverage = {};

  if (isRentCustomized && values.monthsOfCoverage != null) {
    coverage.lease_guarantee = {
      coverage_unit: 'months',
      coverage_amount: values.monthsOfCoverage,
    };
  }

  if (isDepositCustomized && values.securityDepositReplacementAmount != null) {
    coverage.security_deposit_replacement = {
      coverage_unit: 'amount',
      coverage_amount: values.securityDepositReplacementAmount,
    };
  }

  return coverage.lease_guarantee || coverage.security_deposit_replacement ? coverage : undefined;
};

export const buildInviteLeaseDetailsPayload = (values: Renter) => {
  const { address } = values;

  const coverage = buildCoverage(values);

  return {
    property_id: address.property_alpha_id,
    address_id: address.address_alpha_id,
    screening_status: SCREENING_STATUS_MAP?.[values.approvalStatus!],
    gross_monthly_rent: values.grossMonthlyRent,
    prepaid_rent: values.prepaidRent,
    free_rent: values.freeRent,
    lease_start: formatCalendarDate(values.leaseStart),
    lease_end: formatCalendarDate(values.leaseEnd),
    unit: values.apartmentNumber,
    applicants: values.applicantEmails.map(({ value }) => ({ email: value })),
    ...(coverage && { coverage }),
  };
};
